// components/MembersTable.jsx
import React from "react";
import { User } from "lucide-react";
import { Pagination } from "./common/PaginationNumber";
import Cookies from "js-cookie"; // Add this import

const MembersTable = ({
  members,
  isLoading,
  isError,
  onOpenModal,
  currentPage,
  entriesPerPage,
  totalPages,
  onPageChange,
  permissionsData, // New prop
  canEdit, // New prop
}) => {
  const role = Cookies.get("role");

  // Permission checks
  let canManageRoles = false;

  if (role === "admin") {
    canManageRoles = true;
  } else if (role === "maintainer") {
    const modulePermissions = permissionsData?.permissions?.find(
      (p) => p.module === "Leadership Role"
    );

    canManageRoles = !!modulePermissions?.edit;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading members...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-red-500">Failed to fetch members.</p>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-center">
          <User size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No members found.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-t-2xl overflow-hidden shadow-sm min-h-[300px] mb-6 border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#4F22D8] text-white">
              <tr>
                <th className="py-4 px-4 text-left font-semibold border-r border-purple-400 w-16">
                  No
                </th>
                <th className="py-4 px-4 text-left font-semibold border-r border-purple-400 min-w-[200px]">
                  Name
                </th>
                <th className="py-4 px-4 text-left font-semibold border-r border-purple-400 min-w-[140px]">
                  Membership ID
                </th>
                <th className="py-4 px-4 text-left font-semibold border-r border-purple-400 min-w-[200px]">
                  Current Roles
                </th>
                {/* Conditionally render Actions header */}
                {canManageRoles && (
                  <th className="py-4 px-4 text-left font-semibold min-w-[120px]">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {members.map((member, idx) => (
                <MemberTableRow
                  key={member.id}
                  member={member}
                  index={idx}
                  currentPage={currentPage}
                  entriesPerPage={entriesPerPage}
                  onOpenModal={onOpenModal}
                  canManageRoles={canManageRoles}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 mb-6">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={onPageChange}
            showPages={5}
          />
        </div>
      )}
    </>
  );
};

const MemberTableRow = ({
  member,
  index,
  currentPage,
  entriesPerPage,
  onOpenModal,
  canManageRoles,
}) => {
  const handleManageRoles = () => {
    if (!canManageRoles) {
      // Could show a toast here if needed
      console.warn("No permission to manage roles");
      return;
    }
    onOpenModal(member);
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors duration-150">
      <td className="px-4 py-4 border-r border-gray-200 text-gray-700 font-medium">
        {(currentPage - 1) * entriesPerPage + index + 1}
      </td>
      <td className="px-4 py-4 border-r border-gray-200">
        <div className="flex items-center">
          <span className="text-gray-900 font-medium truncate">
            {member.name}
          </span>
        </div>
      </td>
      <td className="px-4 py-4 border-r border-gray-200">
        <span className="text-gray-700 font-mono text-sm">
          {member.membershipId ?? "-"}
        </span>
      </td>
      <td className="px-4 py-4 border-r border-gray-200">
        <div className="flex flex-col gap-1.5">
          {member.roles && member.roles.length > 0 ? (
            <>
              {member.roles &&
              member.roles.flatMap((lr) => lr.roles || []).length > 0 ? (
                member.roles
                  .flatMap((lr) => lr.roles || [])
                  .sort((a, b) => a.from - b.from)
                  .map((role, roleIndex) => (
                    <span
                      key={role._id || roleIndex}
                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full max-w-fit"
                    >
                      {role.designation?.name}{" "}
                      <span className="ml-1 text-gray-600">
                        ({role.from} - {role.isCurrent ? "Present" : role.to})
                      </span>
                    </span>
                  ))
              ) : (
                <span className="text-xs text-gray-500 italic">
                  No roles assigned
                </span>
              )}
            </>
          ) : (
            <span className="text-xs text-gray-500 italic">
              No roles assigned
            </span>
          )}
        </div>
      </td>

      {/* Conditionally render Actions column */}
      {canManageRoles && (
        <td className="px-4 py-4">
          <button
            onClick={handleManageRoles}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#4F22D8] hover:text-white hover:bg-[#4F22D8] border border-[#4F22D8] rounded-lg transition-all duration-200 hover:shadow-sm"
          >
            <User size={14} />
            Manage Roles
          </button>
        </td>
      )}
    </tr>
  );
};

export default MembersTable;
